import { isEqual, without } from 'lodash';
import { DateTime } from 'luxon';
import {
  ComponentPropsWithoutRef,
  MouseEvent,
  MouseEventHandler,
  useEffect,
  useRef,
  useState,
} from 'react';
import OutsideClickHandler from 'react-outside-click-handler';
import styled from 'styled-components';

import EditableItem from 'dashboard/components/FormControlPilledInput/EditableItem';
import Pill from 'dashboard/components/FormControlPilledInput/Pill';
import {
  Control,
  ControlConfig,
  ControlConfigDateTimeRangePicker,
  ControlConfigSelect,
  formatDateTimeRangeValue,
  Value,
} from 'dashboard/components/FormControlPilledInput/types';
import { DateTimeValue, TextValue, ValueType } from 'dashboard/models/graph-query';
import inputMixin, { InputMixinProps } from 'design-system/mixins/input';
import { BASE } from 'design-system/variables/spacing';

export interface InputProps extends ComponentPropsWithoutRef<'input'>, InputMixinProps {}

const FakeInputContainer = styled.div<InputProps & { isCompact: boolean }>`
  ${inputMixin};

  position: relative;
  cursor: pointer;
  height: unset;

  ${({ isCompact }) => {
    if (isCompact) {
      return `
        padding-top: ${BASE - 3.25}px;
        padding-bottom: ${BASE - 3.25}px;
        line-height: 20px;
    `;
    }
    return `
      padding-top: ${BASE - 2}px;
      padding-bottom: ${BASE - 2}px;
    `;
  }};
`;

const FakeInput = styled.input<InputProps & { isCompact: boolean }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  opacity: 0;
`;

export const PlaceholderText = styled.span`
  color: ${({ theme }) => theme.color.text.tertiary};
`;

interface FormControlPilledInputProps {
  controlConfig: ControlConfig;
  hasError: boolean;
  onChange?: (value: Value[]) => void;
  placeholder: string;
  value?: Value[] | null;
  isCompact?: boolean;
  onValueChange?: () => void;
}

const FormControlPilledInput = ({
  controlConfig,
  hasError,
  onChange,
  placeholder,
  value: initialValue = [],
  // Remove this flag once we've ramped GraphV2 to 100%
  isCompact = false,
  onValueChange,
}: FormControlPilledInputProps) => {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const [inputValue, setInputValue] = useState<Value[]>(initialValue);
  const prevInputValueJson = useRef<Value[] | null>(inputValue);
  const [editPillIndex, setEditPillIndex] = useState<number | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const fakeInputRef = useRef<HTMLDivElement>();
  const newItemInputRef = useRef<HTMLInputElement>();
  const editItemInputRef = useRef<HTMLInputElement>();

  const isAllOptionSelected =
    inputValue.length === 1 &&
    controlConfig.type === Control.Select &&
    !Array.isArray(inputValue[0]) &&
    inputValue[0]['raw-value'] === controlConfig.allOption?.value;

  useEffect(() => {
    if (controlConfig.type !== 'select') {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      setInputValue(initialValue);
    } else {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const initialValues = initialValue.map((i: TextValue) => i.value);

      const allValues = controlConfig.options.map((o) => o.value);

      if (isEqual(initialValues, allValues) && controlConfig.allOption) {
        // if every option is selected, and an `allOption` is available, use that instead
        setInputValue([
          {
            'raw-value': controlConfig.allOption.value,
            value: controlConfig.allOption.value,
            type: ValueType.Text,
          },
        ]);
      } else {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        setInputValue(initialValue);
      }
    }
  }, [initialValue]);

  useEffect(() => {
    // Value objects (unless simple JS types) and Value[] arrays do not in general support
    // strict equality semantics (===) as required by React hooks, so we track the JSON form
    // of any new proposed state and compare it with the JSON form of the previous state to
    // decide if we need to fire an onChange. This prevents an infinite trigger of hooks.
    if (!isEqual(prevInputValueJson.current, inputValue)) {
      prevInputValueJson.current = inputValue;
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      onChange(inputValue);
    }
    // We do not use exhaustive deps on this because we use `prevInputValueJson` only
    // to store the previously computed JSON version of the inputValue we're tracking
    // and we're the only ones changing it (here in this hook).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue, onChange]);

  const deleteItem = (i: number) => () => {
    const newInputValue = [...inputValue.slice(0, i), ...inputValue.slice(i + 1)];
    setInputValue(newInputValue);
    setEditPillIndex(null);
    onValueChange?.();
    setTimeout(() => {
      newItemInputRef.current?.focus();
    }, 0);
  };

  const deleteLastItem = inputValue
    ? deleteItem(inputValue.length - 1)
    : () => {
        // noop
      };

  const handlePillValueClick = (index: number) => (e: MouseEvent<HTMLDivElement>) => {
    // Don't propagate event to containing FakeInputContainer
    e.preventDefault();
    e.stopPropagation();

    setIsFocused(true);

    // Pills from Select shouldn't be editable
    if (controlConfig.type !== Control.Select) {
      setEditPillIndex(index);

      setTimeout(() => {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        editItemInputRef.current.focus();
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        editItemInputRef.current.select();
      }, 0);
    }
  };

  const handlePillClearClick = (index: number) => (_e: MouseEvent<HTMLDivElement>) => {
    setInputValue(without(inputValue, inputValue[index]));
    onValueChange?.();
  };

  const handleFakeInputContainerClick: MouseEventHandler = () => {
    if (controlConfig.type === Control.None) {
      return;
    }

    setIsFocused(true);

    if (controlConfig.multi || pilledElements.length === 0) {
      setEditPillIndex(null);
      newItemInputRef.current?.focus();
    } else {
      // Auto-select the pilled element, if there's only one pilled input allowed, and there already is one.
      setEditPillIndex(0);
    }
  };

  const handleFakeInputFocus = () => {
    // equivalent to clicking the pill
    setIsFocused(true);
    setEditPillIndex(0);
    setTimeout(() => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      editItemInputRef.current.focus();
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      editItemInputRef.current.select();
    }, 0);
  };

  const handleOutsideClick = () => {
    newItemInputRef.current?.blur();
    editItemInputRef.current?.blur();

    if (editPillIndex != null) {
      setEditPillIndex(null);
    }

    setIsFocused(false);
  };

  const newItemHandleValueChange = (value: Value) => {
    if (controlConfig.type === Control.None) {
      return;
    }
    setInputValue([...inputValue, value]);

    if (controlConfig.multi) {
      setTimeout(() => {
        newItemInputRef.current?.focus();
      }, 0);
    }
    onValueChange?.();
  };

  const editItemHandleValueChange = (value: Value) => {
    const newInputValue = [...inputValue];
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    newInputValue[editPillIndex] = value;
    setInputValue(newInputValue);
    setTimeout(() => {
      setEditPillIndex(null);
    }, 0);
    onValueChange?.();
  };

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const processControlConfig = (controlConfig: ControlConfig, currentValue: Value = null) => {
    if (controlConfig.type === Control.Select && controlConfig.multi) {
      if (isAllOptionSelected) {
        // if the "all" option is currently selected, don't allow any other options to be selected
        return {
          ...controlConfig,
          options: [],
        };
      }
      const newConfig = Object.assign({}, controlConfig) as ControlConfigSelect;
      // safe to typecast as select only uses TextValue
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const selectedValues = inputValue.map((v: TextValue) => v['raw-value']);
      newConfig.options = controlConfig.options.filter((option) => {
        return (
          !selectedValues.includes(option.value) ||
          (currentValue && (currentValue as TextValue)['raw-value'] === option.value)
        );
      });
      if (inputValue.length === 0 && controlConfig.allOption) {
        // If no options are selected, allow the allOption to be picked
        newConfig.options.unshift(controlConfig.allOption);
      }
      return newConfig;
    }

    return controlConfig;
  };

  // The display value should be derived from the available options
  const selectLabels: { [key: string]: string } = {};
  if (controlConfig.type === Control.Select) {
    controlConfig.options.forEach((o) => {
      selectLabels[o.value] = o.label;
    });
    if (controlConfig.allOption) {
      selectLabels[controlConfig.allOption.value] = controlConfig.allOption.label;
    }
  }

  const pilledElements = inputValue.map((iv: Value, index: number) => {
    if (index === editPillIndex) {
      return (
        <EditableItem
          controlConfig={processControlConfig(controlConfig, inputValue[editPillIndex])}
          deleteItem={deleteItem(editPillIndex)}
          key={`edit-${index}`}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          inputRef={editItemInputRef}
          isLast={false}
          onValueChange={editItemHandleValueChange}
          value={inputValue[editPillIndex]}
          isCompact={isCompact}
          isVariable={'variable' in iv ? iv.variable : false}
        />
      );
    }

    let pillColor;
    let pillValue;

    // Handle ranges, i.e. [DateTimeValue, DateTimeValue];
    if (Array.isArray(iv)) {
      const ivTypes = iv.map((v: DateTimeValue) => v.type);
      if (!isEqual(ivTypes, [ValueType.DateTime, ValueType.DateTime])) {
        throw new Error(`Presumed date time range array was incorrect: ${ivTypes}`);
      }

      const includeTimeInFormat = !(controlConfig as ControlConfigDateTimeRangePicker).fixTime;
      let lower = iv[0];
      let upper = iv[1];
      // TODO(jjowdy): Unify this checking logic.
      if (lower.type !== 'datetime' || upper.type !== 'datetime') {
        throw new Error();
      }

      lower = lower as DateTimeValue;
      upper = upper as DateTimeValue;

      if (
        lower.errored ||
        upper.errored ||
        (lower.moment &&
          upper.moment &&
          DateTime.fromISO(lower.moment) >= DateTime.fromISO(upper.moment))
      ) {
        pillColor = 'red';
      } else if (lower.variable || upper.variable) {
        pillColor = 'purple';
      } else {
        pillColor = 'gray';
      }

      pillValue = formatDateTimeRangeValue(lower, upper, includeTimeInFormat);
    } else if (
      // HANDLE SINGLE VALUES
      iv.type === 'text' ||
      iv.type === 'number' ||
      iv.type === 'date' ||
      iv.type === 'datetime' ||
      iv.type === 'range'
    ) {
      if (iv.errored) {
        pillColor = 'red';
        pillValue = iv['raw-value'];
      } else if (iv.variable) {
        pillColor = 'purple';
        pillValue = (
          <>
            <b>{`${iv.name}: `}</b>
            {iv.value || <PlaceholderText>{placeholder}</PlaceholderText>}
          </>
        );
      } else {
        pillColor = 'gray';
        const getPillValue = (val: Value) => {
          if (Array.isArray(val)) {
            return;
          }

          switch (val.type) {
            case 'date':
              return DateTime.fromISO(val.moment).toUTC().toFormat('yyyy-MM-dd');
            case 'datetime':
              return DateTime.fromISO(val.moment).toFormat('yyyy-MM-dd HH:mm:ss');
            default:
              return val.value;
          }
        };

        if (iv.type === 'range' && iv.range) {
          pillValue = `${getPillValue(iv.range.lower)} - ${getPillValue(iv.range.upper)}`;
        } else if (controlConfig.type === Control.Select && iv['raw-value'] in selectLabels) {
          pillValue = selectLabels[iv['raw-value']];
        } else {
          pillValue = getPillValue(iv);
        }
      }
    }

    if (pillColor && pillValue) {
      return (
        <Pill
          key={index}
          color={pillColor}
          isCompact={isCompact}
          clearable={isFocused}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          handleClick={handlePillValueClick(index)}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          handleClear={handlePillClearClick(index)}
        >
          {pillValue}
        </Pill>
      );
    }
  });

  if (controlConfig.type === Control.None) {
    return <></>;
  }

  const isMulti = controlConfig.multi && !isAllOptionSelected;

  return (
    <OutsideClickHandler onOutsideClick={handleOutsideClick}>
      <FakeInputContainer
        id='graph-query-bar__fake-input'
        onClick={handleFakeInputContainerClick}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        ref={fakeInputRef}
        hasError={hasError}
        isCompact={isCompact}
      >
        {!controlConfig.multi && pilledElements.length >= 1 && editPillIndex == null ? (
          <FakeInput isCompact={isCompact} type='text' onFocus={handleFakeInputFocus} />
        ) : null}
        {pilledElements}
        {isMulti || (!isMulti && pilledElements.length < 1) ? (
          <EditableItem
            controlConfig={processControlConfig(controlConfig)}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            inputRef={newItemInputRef}
            isLast={true}
            key={JSON.stringify(inputValue)}
            onValueChange={newItemHandleValueChange}
            deleteLastItem={deleteLastItem}
            placeholder={pilledElements.length < 1 && inputValue.length === 0 ? placeholder : null}
            isCompact={isCompact}
          />
        ) : null}
      </FakeInputContainer>
    </OutsideClickHandler>
  );
};

export default FormControlPilledInput;
